import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Bell } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { api, fmtDate } from "@/lib/api";

export default function NotificationBell({ dark = false }) {
  const nav = useNavigate();
  const { data = [], refetch } = useQuery({ queryKey: ["notifications"], queryFn: () => api.get("/notifications").then((r) => r.data), refetchInterval: 60000 });
  const unread = data.filter((n) => !n.read).length;
  const open = async (n) => {
    if (!n.read) await api.post(`/notifications/${n.id}/read`);
    refetch();
    if (n.link) nav(n.link);
  };
  const readAll = () => api.post("/notifications/read").then(() => refetch());
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className={`relative p-2 rounded-full ${dark ? "hover:bg-white/10 text-[#FAF8F2]" : "hover:bg-slate-100 text-slate-600"}`} data-testid="notifications-btn" aria-label={`Notifications${unread ? `, ${unread} unread` : ""}`}>
          <Bell className="h-5 w-5" />
          {unread > 0 && <span className="absolute top-0.5 right-0.5 h-4 min-w-4 px-1 rounded-full bg-[#D4AF63] text-[#00281F] text-[10px] font-bold grid place-items-center" data-testid="notifications-unread-count">{unread}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[22rem] max-w-[92vw] p-0" data-testid="notifications-panel">
        <div className="px-4 py-3 border-b flex items-center justify-between">
          <span className="font-semibold text-sm">Notifications</span>
          {unread > 0 && <button className="text-xs font-semibold text-teal-700 hover:underline" onClick={readAll} data-testid="notifications-mark-all-read">Mark all read</button>}
        </div>
        <div className="max-h-96 overflow-auto">
          {data.length === 0 ? <div className="p-4 text-sm text-slate-500">You're all caught up.</div> : data.map((n) => (
            <button key={n.id} onClick={() => open(n)} data-testid={`notification-item-${n.id}`}
              className={`w-full text-left px-4 py-3 border-b last:border-0 hover:bg-slate-50 flex gap-3 ${n.read ? "text-slate-500" : "text-slate-900"}`}>
              <span className={`mt-1.5 h-2 w-2 rounded-full shrink-0 ${n.read ? "bg-transparent" : n.type === "carematch_match" ? "bg-[#D4AF63]" : "bg-teal-600"}`} />
              <span className="min-w-0">
                <span className={`block text-sm ${n.read ? "" : "font-semibold"}`}>{n.title}</span>
                {n.body && <span className="block text-xs text-slate-500 mt-0.5">{n.body}</span>}
                <span className="block text-[11px] text-slate-400 mt-1">{fmtDate(n.created_at)}{n.type === "carematch_match" && <span className="ml-2 font-semibold text-[#9a7a2f]">View My Match →</span>}</span>
              </span>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
